/**
 * 宠物状态机 Hook
 * 管理动画状态转换
 */
import { useReducer, useCallback, useEffect, useRef } from 'react'
import type { PetAnimationState, PetEvent } from './types'
import { IDLE_TIMEOUT, RAPID_CLICK_COUNT, RAPID_CLICK_WINDOW } from './types'

interface PetState {
  animationState: PetAnimationState
}

function petReducer(state: PetState, event: PetEvent): PetState {
  const { animationState } = state

  switch (event) {
    case 'DRAG_START':
      return { animationState: 'dragging' }

    case 'DRAG_END':
      return { animationState: 'idle' }

    case 'ANIMATION_END':
      // 临时动画播完回归 idle
      if (animationState !== 'idle' && animationState !== 'sleepy' && animationState !== 'dragging') {
        return { animationState: 'idle' }
      }
      return state

    case 'RAPID_CLICK':
      if (animationState === 'idle' || animationState === 'sleepy') {
        return { animationState: 'angry' }
      }
      return state

    case 'CLICK':
      if (animationState === 'idle' || animationState === 'sleepy') {
        return { animationState: Math.random() > 0.5 ? 'happy' : 'excited' }
      }
      return state

    case 'DOUBLE_CLICK':
      if (animationState === 'idle' || animationState === 'sleepy') {
        return { animationState: 'working' }
      }
      return state

    case 'IDLE_TIMEOUT':
      if (animationState === 'idle') {
        return { animationState: 'sleepy' }
      }
      return state

    case 'HOVER':
      if (animationState === 'sleepy') {
        return { animationState: 'idle' }
      }
      return state

    default:
      return state
  }
}

export function usePetStateMachine() {
  const [state, dispatch] = useReducer(petReducer, { animationState: 'idle' })
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clickTimesRef = useRef<number[]>([])

  // 重置无操作计时器
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
    }
    idleTimerRef.current = setTimeout(() => {
      dispatch('IDLE_TIMEOUT')
    }, IDLE_TIMEOUT)
  }, [])

  // 处理点击事件（含快速点击检测）
  const handleClick = useCallback(() => {
    const now = Date.now()
    clickTimesRef.current.push(now)

    // 清除超出时间窗口的点击记录
    clickTimesRef.current = clickTimesRef.current.filter(
      (t) => now - t < RAPID_CLICK_WINDOW
    )

    if (clickTimesRef.current.length >= RAPID_CLICK_COUNT) {
      clickTimesRef.current = []
      dispatch('RAPID_CLICK')
    } else {
      dispatch('CLICK')
    }

    resetIdleTimer()
  }, [resetIdleTimer])

  // 处理双击事件
  const handleDoubleClick = useCallback(() => {
    clickTimesRef.current = []
    dispatch('DOUBLE_CLICK')
    resetIdleTimer()
  }, [resetIdleTimer])

  // 处理悬停事件
  const handleHover = useCallback(() => {
    dispatch('HOVER')
    resetIdleTimer()
  }, [resetIdleTimer])

  // 处理拖拽开始
  const handleDragStart = useCallback(() => {
    dispatch('DRAG_START')
  }, [])

  // 处理拖拽结束
  const handleDragEnd = useCallback(() => {
    dispatch('DRAG_END')
    resetIdleTimer()
  }, [resetIdleTimer])

  // 处理动画结束
  const handleAnimationEnd = useCallback(() => {
    dispatch('ANIMATION_END')
    resetIdleTimer()
  }, [resetIdleTimer])

  // 初始化无操作计时器
  useEffect(() => {
    resetIdleTimer()
    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
      }
    }
  }, [resetIdleTimer])

  return {
    animationState: state.animationState,
    handleClick,
    handleDoubleClick,
    handleHover,
    handleDragStart,
    handleDragEnd,
    handleAnimationEnd,
  }
}
